'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle, Lock, Sparkles } from 'lucide-react';

interface GossipForm {
  gossip: string;
  category: string;
  nickname: string;
}

const CATEGORIES = ['Amor', 'Trabajo', 'Familia', 'Vecinos', 'Famosos', 'Otro'];
const MAX_CHARS = 500;

export default function SubmitGossipForm() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<GossipForm>({ defaultValues: { category: '', nickname: '' } });

  const gossipText = watch('gossip') || '';
  const remaining = MAX_CHARS - gossipText.length;

  const onSubmit = async (data: GossipForm) => {
    // BACKEND: POST /api/chismes — create anonymous gossip submission
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="confetti-bg border border-primary/20 rounded-3xl p-10 text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h3 className="text-2xl font-extrabold text-foreground mb-2">
          ¡Chisme enviado con éxito!
        </h3>
        <p className="text-muted-foreground text-base mb-4">
          Tu chisme ya está en la cola. La comunidad votará y si queda top, ¡la anfi lo cuenta en vivo! 🧶🔥
        </p>
        <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-semibold">
          <CheckCircle size={16} />
          Tu chisme es completamente anónimo
        </div>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-6 btn-secondary px-6 py-3 rounded-xl text-sm font-semibold"
        >
          ¿Tienes otro chisme? 🤭
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
      {/* Privacy notice */}
      <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <Lock size={16} className="text-green-600 mt-0.5 shrink-0" />
        <p className="text-xs text-green-700 leading-relaxed">
          <span className="font-semibold">Tu chisme es 100% anónimo.</span> No guardamos tu IP ni datos personales. Nadie sabrá que fuiste tú. 🔒
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Gossip textarea */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            El chisme <span className="text-primary">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Cuéntalo con todos los detalles. Más dramático, mejor. 👀
          </p>
          <div className="relative">
            <textarea
              {...register('gossip', {
                required: 'Escribe tu chisme, no lo guardes para ti 😂',
                minLength: { value: 20, message: 'Muy corto, ¡dale más detalle!' },
                maxLength: { value: MAX_CHARS, message: 'Demasiado largo, acórtalo un poco' },
              })}
              rows={5}
              placeholder="Resulta que mi vecina... 👀"
              className={`w-full bg-input border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition ${
                errors.gossip ? 'border-red-400' : 'border-border'
              }`}
            />
            <div
              className={`absolute bottom-3 right-3 text-xs font-tabular font-medium ${
                remaining < 50 ? 'text-red-500' : 'text-muted-foreground'
              }`}
            >
              {remaining}
            </div>
          </div>
          {errors.gossip && (
            <p className="text-red-500 text-xs mt-1">{errors.gossip.message}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Categoría
          </label>
          <p className="text-xs text-muted-foreground mb-2">Opcional — ayuda a organizar los chismes</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const isSelected = watch('category') === cat;
              return (
                <label
                  key={`cat-${cat}`}
                  className={`cursor-pointer px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                    isSelected
                      ? 'bg-primary text-white border-primary' :'bg-muted border-border text-muted-foreground hover:border-primary/50 hover:text-primary'
                  }`}
                >
                  <input
                    {...register('category')}
                    type="radio"
                    value={cat}
                    className="sr-only"
                  />
                  {cat}
                </label>
              );
            })}
          </div>
        </div>

        {/* Nickname */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            Apodo (opcional)
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Si quieres que la anfi te mencione con un apodo en el live
          </p>
          <input
            {...register('nickname')}
            type="text"
            placeholder="Ej: La vecina del 4to, Anónima de Providencia..."
            className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-base disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Enviando chisme...
            </>
          ) : (
            <>
              <Sparkles size={18} />
              ¡Soltar el Chisme! 🧶
            </>
          )}
        </button>
      </form>
    </div>
  );
}